import React from 'react';
import { GenericFormPage } from '../../components/generic';
import { useClientService, ClientService, useServiceTypesForSelect, useFrequencyTypesForSelect } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const toDateOnly = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const text = String(value).trim();
  if (!text) return undefined;
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDateOnly = (value: string | undefined): Date | null => {
  const normalized = toDateOnly(value);
  if (!normalized) return null;
  const parts = normalized.split('-').map(Number);
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateOnly = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDaysInMonthUtc = (year: number, monthZeroBased: number): number => {
  return new Date(Date.UTC(year, monthZeroBased + 1, 0)).getUTCDate();
};

const addMonthsClampedUtc = (date: Date, monthsToAdd: number): Date => {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  const targetMonthRaw = m + monthsToAdd;
  const targetYear = y + Math.floor(targetMonthRaw / 12);
  const targetMonth = ((targetMonthRaw % 12) + 12) % 12;
  const maxDay = getDaysInMonthUtc(targetYear, targetMonth);
  const nextDay = Math.min(d, maxDay);
  return new Date(Date.UTC(targetYear, targetMonth, nextDay));
};

const addYearsClampedUtc = (date: Date, yearsToAdd: number): Date => {
  const y = date.getUTCFullYear() + yearsToAdd;
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  const maxDay = getDaysInMonthUtc(y, m);
  return new Date(Date.UTC(y, m, Math.min(d, maxDay)));
};

const resolveFrequencyTypeUnit = (
  frequencyTypeId: string | number | undefined,
  frequencyTypeOptions: Array<{ value: string | number; label: string }>
): { unit: 'day' | 'week' | 'month' | 'year'; multiplier: number } | null => {
  if (frequencyTypeId === undefined || frequencyTypeId === null || String(frequencyTypeId).trim() === '') return null;
  const selected = frequencyTypeOptions.find((opt) => String(opt.value) === String(frequencyTypeId));
  const name = (selected?.label || '').toLowerCase();
  if (!name) return null;

  if (name.includes('quarter')) return { unit: 'month', multiplier: 3 };
  if (name.includes('biweek') || name.includes('bi-week') || name.includes('fortnight')) return { unit: 'week', multiplier: 2 };
  if (name.includes('semi-month') || name.includes('semimonth')) return { unit: 'day', multiplier: 15 };
  if (name.includes('annual') || name.includes('year') || name.includes('yr')) return { unit: 'year', multiplier: 1 };
  if (name.includes('month') || name.includes('mo')) return { unit: 'month', multiplier: 1 };
  if (name.includes('week') || name.includes('wk')) return { unit: 'week', multiplier: 1 };
  if (name.includes('day') || name.includes('daily')) return { unit: 'day', multiplier: 1 };

  return null;
};

const calculateRenewalDate = (
  startDateRaw: string | undefined,
  frequencyRaw: string | number | undefined,
  frequencyTypeId: string | number | undefined,
  frequencyTypeOptions: Array<{ value: string | number; label: string }>
): string | null => {
  const startDate = parseDateOnly(startDateRaw);
  if (!startDate) return null;

  const frequencyValue = Number(frequencyRaw);
  if (!Number.isFinite(frequencyValue) || frequencyValue <= 0) return null;

  const unitMeta = resolveFrequencyTypeUnit(frequencyTypeId, frequencyTypeOptions);
  if (!unitMeta) return null;

  const totalSteps = Math.floor(frequencyValue * unitMeta.multiplier);
  if (totalSteps <= 0) return null;

  let nextDate: Date;
  switch (unitMeta.unit) {
    case 'day':
      nextDate = new Date(startDate.getTime());
      nextDate.setUTCDate(nextDate.getUTCDate() + totalSteps);
      break;
    case 'week':
      nextDate = new Date(startDate.getTime());
      nextDate.setUTCDate(nextDate.getUTCDate() + totalSteps * 7);
      break;
    case 'month':
      nextDate = addMonthsClampedUtc(startDate, totalSteps);
      break;
    case 'year':
      nextDate = addYearsClampedUtc(startDate, totalSteps);
      break;
    default:
      return null;
  }

  return formatDateOnly(nextDate);
};

const calculatePeriodEndDate = (
  periodStartRaw: string | undefined,
  frequencyRaw: string | number | undefined,
  frequencyTypeId: string | number | undefined,
  frequencyTypeOptions: Array<{ value: string | number; label: string }>
): string | null => {
  const periodStart = parseDateOnly(periodStartRaw);
  if (!periodStart) return null;

  const frequencyValue = Number(frequencyRaw);
  if (!Number.isFinite(frequencyValue) || frequencyValue <= 0) return null;

  const unitMeta = resolveFrequencyTypeUnit(frequencyTypeId, frequencyTypeOptions);
  if (!unitMeta) return null;

  const totalSteps = Math.floor(frequencyValue * unitMeta.multiplier);
  if (totalSteps <= 0) return null;

  let nextCycleStart: Date;
  switch (unitMeta.unit) {
    case 'day':
      nextCycleStart = new Date(periodStart.getTime());
      nextCycleStart.setUTCDate(nextCycleStart.getUTCDate() + totalSteps);
      break;
    case 'week':
      nextCycleStart = new Date(periodStart.getTime());
      nextCycleStart.setUTCDate(nextCycleStart.getUTCDate() + totalSteps * 7);
      break;
    case 'month':
      nextCycleStart = addMonthsClampedUtc(periodStart, totalSteps);
      break;
    case 'year':
      nextCycleStart = addYearsClampedUtc(periodStart, totalSteps);
      break;
    default:
      return null;
  }

  nextCycleStart.setUTCDate(nextCycleStart.getUTCDate() - 1);
  return formatDateOnly(nextCycleStart);
};

const Form: React.FC = () => {
  const { options: serviceTypeOptions } = useServiceTypesForSelect();
  const { options: rawFrequencyTypeOptions } = useFrequencyTypesForSelect();
  const configWithOptions = (() => {
    const frequencyTypeOptions: Array<{ value: string | number; label: string }> = [
      { value: '', label: 'None' },
      ...rawFrequencyTypeOptions,
    ];

    const updateRenewalFromFrequency = (
      _value: unknown,
      _setFieldValue: (name: string, value: unknown) => void,
      setMultipleFieldValues: (updates: Record<string, unknown>) => void,
      getFormData?: () => Record<string, unknown>
    ) => {
      const formData = getFormData?.() ?? {};
      const nextRenewalDate = calculateRenewalDate(
        typeof formData.serviceStartDate === 'string' ? formData.serviceStartDate : undefined,
        typeof formData.serviceFreq === 'string' || typeof formData.serviceFreq === 'number' ? formData.serviceFreq : undefined,
        typeof formData.serviceFreqType === 'string' || typeof formData.serviceFreqType === 'number' ? formData.serviceFreqType : undefined,
        frequencyTypeOptions
      );
      if (!nextRenewalDate) return;
      setMultipleFieldValues({
        serviceEndDate: nextRenewalDate,
      });
    };

    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'serviceStartDate') {
        return {
          ...field,
          onChangeSideEffect: updateRenewalFromFrequency,
        };
      }
      if (field.name === 'serviceFreq') {
        return {
          ...field,
          onChangeSideEffect: updateRenewalFromFrequency,
        };
      }
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client',
          options: [],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'serviceType') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Service Type',
          placeholder: 'Select service type',
          options: serviceTypeOptions,
        };
      }
      if (field.name === 'serviceCategory') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Service Category',
          placeholder: 'Select service category',
          required: true,
          options: [],
          apiEndpoint: '/p-crm-service-category',
          labelField: 'psc_descr',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'serviceFreqType') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Frequency Type',
          placeholder: 'Select frequency type (optional)',
          options: frequencyTypeOptions,
          onChangeSideEffect: updateRenewalFromFrequency,
        };
      }
      if (field.name === 'serviceTags') {
        return {
          ...field,
          type: 'multiselect' as const,
          label: 'Tags',
          placeholder: 'Select tags (optional)',
          options: [],
          apiEndpoint: '/tags',
          labelField: 'name',
          valueField: 'id' as const,
        };
      }
      return field;
    });

    const renewalActionField = {
      name: 'renewalAction',
      label: 'Renewal',
      type: 'custom' as const,
      customRender: ({ formData, setMultipleFieldValues }: {
        formData?: Record<string, unknown>;
        setMultipleFieldValues?: (updates: Record<string, unknown>) => void;
      }) => {
        const oldEndRaw = typeof formData?.serviceEndDate === 'string' ? formData.serviceEndDate : undefined;
        const oldEndDate = parseDateOnly(oldEndRaw);
        const canRenew = Boolean(oldEndDate);

        const onRenewalClick = () => {
          if (!setMultipleFieldValues || !formData) return;
          if (!oldEndDate) return;

          const newStartDate = new Date(oldEndDate.getTime());
          newStartDate.setUTCDate(newStartDate.getUTCDate() + 1);
          const newStartDateText = formatDateOnly(newStartDate);

          const newEndDateText = calculatePeriodEndDate(
            newStartDateText,
            typeof formData.serviceFreq === 'string' || typeof formData.serviceFreq === 'number' ? formData.serviceFreq : undefined,
            typeof formData.serviceFreqType === 'string' || typeof formData.serviceFreqType === 'number' ? formData.serviceFreqType : undefined,
            frequencyTypeOptions
          );

          const updates: Record<string, unknown> = {
            serviceStartDate: newStartDateText,
          };
          if (newEndDateText) {
            updates.serviceEndDate = newEndDateText;
          }
          setMultipleFieldValues(updates);
        };

        return (
          <div className="md:col-span-2 rounded-lg border border-indigo-100 bg-indigo-50/60 dark:border-indigo-900/40 dark:bg-indigo-950/20 px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-indigo-600/90 dark:text-indigo-300/80">
                  Start = old End + 1 day, End = frequency cycle - 1 day.
                </p>
              </div>
              <button
                type="button"
                onClick={onRenewalClick}
                disabled={!canRenew}
                className="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-white px-3 py-1.5 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200 dark:hover:bg-indigo-900/60"
                title={canRenew ? 'Apply renewal dates' : 'Set End Date first'}
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M15.312 11.424a5 5 0 10-9.81-1.424H3.75a.75.75 0 000 1.5h2.5a.75.75 0 00.75-.75V8.5a.75.75 0 00-1.5 0v.91a3.5 3.5 0 116.87 1.014.75.75 0 001.442.3zM10 14a.75.75 0 01.75.75v.91a3.5 3.5 0 11-6.87-1.014.75.75 0 01-1.442-.3 5 5 0 109.81 1.424h1.752a.75.75 0 000-1.5h-2.5a.75.75 0 00-.75.75v2.25a.75.75 0 001.5 0v-.91A5.486 5.486 0 0010 14z" clipRule="evenodd" />
                </svg>
                Apply
              </button>
            </div>
          </div>
        );
      },
    };

    const fieldsWithRenewalAction = (() => {
      const next = [...fields];
      const endDateIndex = next.findIndex((field) => field.name === 'serviceEndDate');
      if (endDateIndex >= 0) {
        next.splice(endDateIndex + 1, 0, renewalActionField);
      } else {
        next.push(renewalActionField);
      }
      return next;
    })();

    type FormClientService = Omit<ClientService, 'clientId' | 'serviceType' | 'serviceCategory' | 'serviceFreqType' | 'serviceTags'> & {
      clientId?: string | number;
      serviceType?: string | number;
      serviceCategory?: string | number;
      serviceFreqType?: string | number;
      serviceTags?: number[];
    };

    const customMapItemToFormData = (item: ClientService): Partial<FormClientService> => {
      const baseData = mapItemToFormData(item);
      const tagIds = Array.isArray(baseData.serviceTags)
        ? baseData.serviceTags.map((tag: any) => (typeof tag === 'object' && tag?.id ? tag.id : tag))
        : (baseData.serviceTags as any);

      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        serviceType: baseData.serviceType ? String(baseData.serviceType) : '',
        serviceCategory: baseData.serviceCategory ? String(baseData.serviceCategory) : '',
        serviceFreqType: baseData.serviceFreqType ? String(baseData.serviceFreqType) : '',
        serviceTags: Array.isArray(tagIds) ? tagIds : [],
      };
    };

    const customGetInitialFormData = (): Partial<FormClientService> => {
      const baseData = getInitialFormData();
      const tagIds: number[] = Array.isArray(baseData.serviceTags)
        ? baseData.serviceTags.map((tag: any) => (typeof tag === 'object' && tag?.id ? tag.id : tag)).filter((id: any): id is number => typeof id === 'number')
        : [];

      return {
        ...baseData,
        clientId: '',
        serviceType: '',
        serviceCategory: '',
        serviceFreqType: '',
        serviceTags: tagIds,
      };
    };

    const convertToNumber = (value: string | number | undefined): number | undefined => {
      if (value === undefined || value === null || String(value) === '') return undefined;
      return Number(value);
    };

    const toISODate = (value: string | undefined): string | undefined => {
      if (value === undefined || value === null || String(value).trim() === '') return undefined;
      const s = String(value).trim();
      if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(s)) return s.includes('T') ? s : `${s}T00:00:00.000Z`;
      const d = new Date(value);
      return isNaN(d.getTime()) ? undefined : d.toISOString();
    };

    const toBackendPayload = (data: Partial<FormClientService>): Record<string, unknown> => {
      const clientId = convertToNumber(data.clientId);
      const serviceType = convertToNumber(data.serviceType);
      const serviceCategory = convertToNumber(data.serviceCategory);
      const serviceFreqType = convertToNumber(data.serviceFreqType);
      const payload: Record<string, unknown> = {};
      if (clientId !== undefined) payload.clientId = clientId;
      if (serviceType !== undefined) payload.serviceType = serviceType;
      if (serviceCategory !== undefined) payload.serviceCategory = serviceCategory;
      if (serviceFreqType !== undefined) payload.serviceFreqType = serviceFreqType;
      if (data.serviceDescr !== undefined) payload.serviceDescr = data.serviceDescr ?? '';
      const startDate = toISODate(data.serviceStartDate);
      if (startDate) payload.serviceStartDate = startDate;
      const endDate = toISODate(data.serviceEndDate);
      if (endDate) payload.serviceEndDate = endDate;
      const termDate = toISODate(data.serviceTerminationDate);
      if (termDate !== undefined) payload.serviceTerminationDate = termDate;
      if (data.serviceFreq !== undefined && String(data.serviceFreq).trim() !== '') payload.serviceFreq = Number(data.serviceFreq);
      if (data.serviceActive !== undefined) payload.serviceActive = Boolean(data.serviceActive);
      if (data.serviceNotes !== undefined) payload.serviceNotes = data.serviceNotes ?? '';
      if (data.serviceCommonNote !== undefined) payload.serviceCommonNote = data.serviceCommonNote ?? '';
      if (data.serviceCost !== undefined && String(data.serviceCost).trim() !== '') payload.serviceCost = Number(data.serviceCost);
      if (data.serviceInvoice !== undefined && String(data.serviceInvoice).trim() !== '') payload.serviceInvoice = Number(data.serviceInvoice);
      if (data.serviceProfit !== undefined && String(data.serviceProfit).trim() !== '') payload.serviceProfit = Number(data.serviceProfit);
      const createdDate = toISODate(data.serviceCreatedDate);
      if (createdDate) payload.serviceCreatedDate = createdDate;
      return payload;
    };

    const customCreate = async (data: Partial<FormClientService>) => {
      const processedData = toBackendPayload(data) as Partial<ClientService>;
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormClientService>) => {
      const processedData = toBackendPayload(data) as Partial<ClientService>;
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields: fieldsWithRenewalAction,
      formLayout: {
        ...moduleConfig.formLayout,
        sections: moduleConfig.formLayout.sections.map((section) => {
          if (section.id !== 'dates') return section;
          const hasRenewalField = section.fields.includes('renewalAction');
          return {
            ...section,
            fields: hasRenewalField ? section.fields : [...section.fields, 'renewalAction'],
          };
        }),
      },
      getInitialFormData: customGetInitialFormData as () => Partial<ClientService>,
      mapItemToFormData: customMapItemToFormData as (item: ClientService) => Partial<ClientService>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<ClientService>) => Promise<ClientService>,
        update: customUpdate as (id: number, data: Partial<ClientService>) => Promise<ClientService>,
      },
    };
  })();

  return (
    <GenericFormPage<ClientService>
      useData={useClientService}
      config={configWithOptions}
    />
  );
};

export default Form;
